import {
  getFirestore,
  getDoc,
  doc,
  setDoc,
  addDoc,
  onSnapshot,
  query,
  collection,
  where,
  writeBatch
} from "firebase/firestore";
import inititializeFirebase from "services/firebase";
import { deleteApp } from "firebase/app";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useAuth } from "./useAuth";
import useEventArticleReader from "database/articles/useEventArticleReader";
import { xor } from "lodash";
import { toast } from "react-toastify";
import useLocalStorage from "helpers/useLocalStorage";
import { patchMysteryArticles } from "helpers/mysteryItem";

const BulkOrdersContext = createContext();
const db = getFirestore();

const BulkOrdersContextProvider = ({ children }) => {
  const { authContextLoaded, user, login } = useAuth();
  const { retrieveArticlesByIDs } = useEventArticleReader();

  const [currentOrderID, setCurrentOrderID] = useLocalStorage(
    "currentOrderID",
    null
  );

  const [isLoading, setIsLoading] = useState(true);
  const [storeID, setStoreID] = useState();
  const [store, setStore] = useState();
  const [courseInfo, setCourseInfo] = useState();
  const [articlesData, setArticlesData] = useState({});
  const [currentOrder, setCurrentOrder] = useState();

  //Fix to delete Firestore instance on page unload
  //This is an issue in Safari
  useEffect(() => {
    const unloadCallback = () => {
      const firebaseApp = inititializeFirebase();
      console.log(firebaseApp);
      deleteApp(firebaseApp)
        .then(function () {
          console.log("App deleted successfully");
        })
        .catch(function (error) {
          console.log("Error deleting app:", error);
        });
    };
    window.addEventListener("beforeunload", unloadCallback);
    return async () => {
      window.removeEventListener("beforeunload", unloadCallback);
    };
  }, []);

  useEffect(() => {
    if (!authContextLoaded || storeID == null) return;

    const q = query(
      collection(db, "courses", courseInfo.id, "events", storeID, "packages")
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const storeData = querySnapshot.docs[0]?.data();
      if (!storeData) {
        setIsLoading(false);
        return;
      }
      if (storeData.type !== "bulk") {
        toast.error("This is not a valid Bulk Purchase Store");
        setIsLoading(false);
      } else {
        const store = querySnapshot.docs[0].data();
        store.id = querySnapshot.docs[0].id;
        store.eventDate = null;
        store.deadline = null;
        setStore(store);
      }
    });

    return () => unsubscribe();
  }, [authContextLoaded, courseInfo?.id, storeID]);

  //Set Article Data on Event Packages
  useEffect(() => {
    if (store == null) return;
    if (
      store.articleData == null ||
      xor(Object.keys(store.articleData), Object.keys(articlesData)).length > 0
    ) {
      setStore((prevState) => ({
        ...prevState,
        articleData: articlesData
      }));
    }
  }, [store, articlesData]);

  //Get Event Articles and modify if the event articles change
  useEffect(() => {
    if (
      store?.allArticles != null &&
      xor(Object.keys(store.allArticles), Object.keys(articlesData)).length > 0
    ) {
      retrieveArticlesByIDs(Object.keys(store.allArticles), store.mysteryArticles)
        .then((articles) => {
          setArticlesData(articles);
        })
        .catch((error) => {
          console.log(error);
          setArticlesData({});
        });
    } else {
      // Same id set: a mystery item's CONTENT may still have changed (an
      // edit doesn't touch allArticles' keys), so the xor guard above never
      // re-fires the Typesense refetch. Patch resolved mystery entries in
      // place instead — patchMysteryArticles returns the same reference
      // when nothing changed, so this is a no-op otherwise.
      setArticlesData((prev) =>
        patchMysteryArticles(prev, store?.mysteryArticles)
      );
    }
  }, [
    store?.allArticles,
    store?.mysteryArticles,
    retrieveArticlesByIDs,
    articlesData
  ]);

  //Get Current Order
  useEffect(() => {
    if (storeID == null || store == null) return;
    // const q = query(
    //   collection(db, "courses", courseInfo.id, "events", storeID, "orders"),
    //   where("isOpen", "==", true)
    // );
    // const unsubscribe = onSnapshot(q, (querySnapshot) => {
    //   if (querySnapshot.docs.length === 1) {
    //     setCurrentOrder({
    //       ...querySnapshot.docs[0].data(),
    //       createdAt: querySnapshot.docs[0].data().createdAt.toDate(),
    //       id: querySnapshot.docs[0].id
    //     });
    //   }
    //   setIsLoading(false);
    // });

    if (currentOrderID == null) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(
        db,
        "courses",
        courseInfo.id,
        "events",
        storeID,
        "orders",
        currentOrderID
      ),
      (doc) => {
        if (doc.exists() && doc.data().isOpen) {
          setCurrentOrder({
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate(),
            id: doc.id
          });
        } else {
          setCurrentOrderID(null);
        }
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [courseInfo?.id, storeID, store, currentOrderID, setCurrentOrderID]);

  const initializeStore = useCallback(
    async (courseID, eventID) => {
      try {
        if (authContextLoaded) {
          if (user == null || !user.possibleTypes?.includes("adidasRep")) {
            await login(`participant@${eventID}.com`, eventID);
          }
          //Get Course Info
          const courseDoc = await getDoc(
            doc(db, "courses", courseID, "public", "generalInfo")
          );
          setCourseInfo({ ...courseDoc.data(), id: courseID });
          setStoreID(eventID);
        }

        return;
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    },
    [login, authContextLoaded, user]
  );

  const lookupStoreCode = useCallback(async (code) => {
    try {
      const codeDoc = await getDoc(doc(db, "storeCodes", code));
      if (codeDoc.exists()) {
        const data = codeDoc.data();
        return { cid: data.cid, eid: data.eid };
      }
      return null;
    } catch (error) {
      console.log("Store code lookup error:", error);
      return null;
    }
  }, []);

  const startNewOrder = useCallback(async () => {
    const batch = writeBatch(db);

    if (currentOrder != null) {
      batch.delete(
        doc(
          db,
          "courses",
          courseInfo?.id,
          "events",
          storeID,
          "orders",
          currentOrder.id
        )
      );
    }

    const newOrder = {
      isOpen: true,
      createdAt: new Date(),
      items: {}
    };
    const newOrderRef = doc(
      collection(db, "courses", courseInfo?.id, "events", storeID, "orders")
    );
    console.log(newOrderRef);
    batch.set(newOrderRef, newOrder);

    await batch.commit();
    setCurrentOrderID(newOrderRef.id);
    return;
  }, [courseInfo?.id, currentOrder, setCurrentOrderID, storeID]);

  const finishCurrentOrder = () => {
    setCurrentOrder(undefined);
    setCurrentOrderID(null);
  };

  return (
    <BulkOrdersContext.Provider
      value={{
        isLoading,
        initializeStore,
        store,
        currentOrder,
        finishCurrentOrder,
        startNewOrder,
        courseInfo,
        lookupStoreCode
      }}
    >
      {children}
    </BulkOrdersContext.Provider>
  );
};

export default BulkOrdersContextProvider;

export const useBulkOrdersDatabase = () => useContext(BulkOrdersContext);
